import { profileMenu } from "../data/menu.js";
import { formatOutput } from "../dsl/format-catalog.js";
import { resolveMenuFormat } from "../dsl/template.js";
import { createPrng } from "../random/prng.js";
import { resolveTokens } from "./resolve-tokens.js";
import { renderNode } from "./render-nodes.js";
/**
 * Renders a frozen design document: ordered pages, no pagination/selection.
 */
export function renderFrozenBook(input) {
    const startedAt = Date.now();
    const { instanceId, menu, template } = input;
    const seed = input.seed ?? instanceId;
    const pagesSpec = template.pages ?? [];
    const profile = profileMenu(menu);
    const brand = mergeBrand(menu.restaurant.brand, input.brandOverrides);
    const tokenResolution = resolveTokens(template, brand);
    const assets = new Map(template.assets.map((asset) => [asset.id, asset.url]));
    const diagnostics = [...tokenResolution.diagnostics];
    const pages = pagesSpec.map((page, index) => renderFrozenPage({
        page,
        index,
        instanceId,
        template,
        menu,
        profile,
        tokens: tokenResolution.tokens,
        assets,
        seed,
        diagnostics,
        showHiddenNodes: input.showHiddenNodes,
        ghostEmptyRepeaters: input.ghostEmptyRepeaters,
    }));
    return {
        instanceId,
        templateVersionId: template.id,
        variantId: input.variantId,
        format: resolveMenuFormat(template),
        output: formatOutput(resolveMenuFormat(template)),
        seed,
        canvas: template.canvas,
        tokens: tokenResolution.tokens,
        fonts: Object.values(tokenResolution.tokens.fonts),
        pages,
        diagnostics,
        profile,
        selections: pages.map((page, pageIndex) => ({
            pageIndex,
            blueprintId: page.blueprintId,
            variant: page.variant,
            score: 1,
            reasoning: ['frozen page'],
            rejected: [],
        })),
        elapsedMs: Date.now() - startedAt,
    };
}
function renderFrozenPage(args) {
    const { page, index, instanceId, template, menu, tokens, assets, seed, diagnostics } = args;
    const side = sideForIndex(index, template.canvas.spread);
    const itemIds = [...(page.itemIds ?? [])];
    const prng = createPrng(`${seed}:frozen:${index}`);
    const context = {
        tokens,
        scope: {
            menu: {
                id: menu.id,
                title: menu.title,
                subtitle: menu.subtitle,
                footnote: menu.footnote,
                categoryCount: menu.categories.length,
                categories: menu.categories,
            },
            restaurant: menu.restaurant,
            brand: menu.restaurant.brand,
            page: {
                index,
                number: index + 1,
                side,
                group: page.group,
                title: page.title,
                items: [],
                itemCount: itemIds.length,
                pageInCategory: 0,
                categoryPage: 1,
                pagesInCategory: 1,
                isFirstOfCategory: true,
                isLastOfCategory: true,
            },
            book: { pageCount: template.pages?.length ?? 0, contents: [] },
            profile: args.profile,
        },
        prng,
        diagnostics,
        pageIndex: index,
        pageGroup: page.group,
        menuFormat: resolveMenuFormat(template),
        currency: menu.restaurant.currency,
        locale: menu.restaurant.locale,
        assets,
        availableWidth: template.canvas.width - template.canvas.safeArea * 2,
        itemIds,
        showHiddenNodes: args.showHiddenNodes,
        ghostEmptyRepeaters: args.ghostEmptyRepeaters,
    };
    const body = renderNode(page.root, context);
    return {
        id: `${instanceId}:${page.id}`,
        index,
        side,
        group: page.group,
        /** Matches page id so the constructor can select by blueprint id. */
        blueprintId: page.id,
        variant: page.id,
        categoryId: page.categoryId,
        title: page.title,
        itemIds,
        width: template.canvas.width,
        height: template.canvas.height,
        root: pageFrame(index, template, tokens, body ? [body] : []),
    };
}
function pageFrame(index, template, tokens, children) {
    const { canvas } = template;
    return {
        id: `page-${index}`,
        tag: 'box',
        style: {
            position: 'relative',
            width: `${canvas.width}px`,
            height: `${canvas.height}px`,
            overflow: 'hidden',
            backgroundColor: tokens.colors.surface ?? '#ffffff',
            color: tokens.colors.onSurface ?? '#111111',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
        },
        children,
    };
}
function sideForIndex(index, spread) {
    if (!spread)
        return 'right';
    return index % 2 === 0 ? 'right' : 'left';
}
function mergeBrand(base, overrides) {
    if (!overrides)
        return base;
    return {
        ...base,
        ...Object.fromEntries(Object.entries(overrides).filter(([, value]) => value !== undefined)),
        colorOverrides: { ...base.colorOverrides, ...overrides.colorOverrides },
    };
}
//# sourceMappingURL=render-frozen.js.map